import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash2, X, Mail, Phone, User, MessageSquare, Key, Eye, EyeOff, Copy, Check, Send, Clock, RefreshCw, Calendar, FileText, Star, ChevronRight, Unlock, ShieldAlert, ShieldOff, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import DraggableModal from '../../components/ui/DraggableModal';
import { adminFetch } from '../../utils/adminFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const services = ['Recién Nacido', 'Primera Comunión', 'Boda', 'Embarazo', 'Familia', 'Bebés', 'Bodas', 'Publicidad'];
const statusLabels = { potential: 'Potencial', active: 'Activo', completed: 'Completado' };
const statusColors = { potential: 'bg-amber-500', active: 'bg-green-500', completed: 'bg-blue-500' };
const sessionTypeLabels = {
  recien_nacido: 'Recién Nacido',
  primera_comunion: 'Primera Comunión',
  boda: 'Boda',
  embarazo: 'Embarazo',
  familia: 'Familia'
};

export const AdminClients = () => {
  const [clients, setClients] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [showCredentials, setShowCredentials] = useState(null);
  const [showClientDetail, setShowClientDetail] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '', status: 'potential', services_interested: [] });
  const [clientCredentials, setClientCredentials] = useState([]);
  const [newCredentials, setNewCredentials] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [extendDays, setExtendDays] = useState(15);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [blockedAccounts, setBlockedAccounts] = useState({});
  const [unlocking, setUnlocking] = useState(null);

  useEffect(() => { fetchData(); fetchBlockedStatus(); }, []);

  const fetchBlockedStatus = async () => {
    try {
      const res = await adminFetch(`${API}/admin/clients/blocked-status`);
      if (res.ok) setBlockedAccounts(await res.json());
    } catch (error) {
      console.error('Error fetching blocked status:', error);
    }
  };

  const handleExport = async () => {
    try {
      const res = await adminFetch(`${API}/admin/clients-export`);
      if (!res.ok) throw new Error('Error');
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clientes_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`${data.length} cliente(s) exportado(s)`);
    } catch {
      toast.error('Error al exportar clientes');
    }
  };


  const quickUnlock = async (clientId, e) => {
    e.stopPropagation();
    setUnlocking(clientId);
    try {
      const res = await adminFetch(`${API}/admin/clients/${clientId}/unlock`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setBlockedAccounts(prev => { const next = {...prev}; delete next[clientId]; return next; });
      } else {
        toast.error('Error al desbloquear');
      }
    } catch { toast.error('Error de conexión'); }
    finally { setUnlocking(null); }
  };

  const fetchData = async () => {
    try {
      const [clientsRes, messagesRes] = await Promise.all([
        adminFetch(`${API}/admin/clients`),
        adminFetch(`${API}/admin/messages`)
      ]);
      if (clientsRes.ok) setClients(await clientsRes.json());
      if (messagesRes.ok) setMessages(await messagesRes.json());
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientDetail = async (clientId) => {
    try {
      const response = await adminFetch(`${API}/admin/clients/${clientId}/detail`);
      if (response.ok) {
        const data = await response.json();
        setClientDetail(data);
        setShowClientDetail(clientId);
      }
    } catch (error) {
      toast.error('Error al cargar detalle del cliente');
    }
  };

  const openModal = (client = null) => {
    if (client) {
      setEditing(client);
      setForm({ ...client, services_interested: client.services_interested || [] });
    } else {
      setEditing(null);
      setForm({ name: '', email: '', phone: '', notes: '', status: 'potential', services_interested: [] });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error('Nombre y email son obligatorios'); return; }
    try {
      const url = editing ? `${API}/admin/clients/${editing.client_id}` : `${API}/admin/clients`;
      const response = await adminFetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!response.ok) throw new Error('Error saving');
      toast.success(editing ? 'Cliente actualizado' : 'Cliente creado');
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este cliente? También se eliminarán sus credenciales de acceso.')) return;
    try {
      await adminFetch(`${API}/admin/clients/${id}`, { method: 'DELETE' });
      toast.success('Cliente eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const convertToClient = async (messageId) => {
    try {
      const response = await adminFetch(`${API}/admin/messages/${messageId}/to-client`, {
        method: 'POST'
      });
      const data = await response.json();
      toast.success(data.message);
      fetchData();
    } catch (error) {
      toast.error('Error al convertir');
    }
  };

  const toggleService = (service) => {
    setForm(prev => ({
      ...prev,
      services_interested: prev.services_interested.includes(service)
        ? prev.services_interested.filter(s => s !== service)
        : [...prev.services_interested, service]
    }));
  };

  // Credentials functions
  const openCredentialsModal = async (client) => {
    setShowCredentials(client);
    setNewCredentials(null);
    setShowPassword(false);
    await fetchClientCredentials(client.client_id);
  };

  const fetchClientCredentials = async (clientId) => {
    try {
      const response = await adminFetch(`${API}/admin/clients/${clientId}/credentials`);
      if (response.ok) {
        setClientCredentials(await response.json());
      }
    } catch (error) {
      console.error('Error fetching credentials:', error);
    }
  };

  const createCredentials = async () => {
    try {
      const response = await adminFetch(`${API}/admin/clients/${showCredentials.client_id}/credentials`, {
        method: 'POST'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Error');
      
      setNewCredentials({
        username: data.username,
        password: data.password
      });
      toast.success('Credenciales creadas');
      fetchClientCredentials(showCredentials.client_id);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const sendCredentialsEmail = async () => {
    setSendingEmail(true);
    try {
      const response = await adminFetch(`${API}/admin/clients/${showCredentials.client_id}/credentials/send-email`, {
        method: 'POST'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Error');
      
      if (data.credentials) {
        // Email failed, show credentials manually
        setNewCredentials({
          username: data.credentials.username,
          password: data.credentials.password,
          expires_at: data.credentials.expires_at
        });
        toast.success('Credenciales activadas - vigencia 15 días');
      } else {
        toast.success(`Email enviado. Válido hasta: ${data.expires_at}`);
        setNewCredentials(null);
      }
      fetchClientCredentials(showCredentials.client_id);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const resendCredentialsEmail = async () => {
    setSendingEmail(true);
    try {
      const response = await adminFetch(`${API}/admin/clients/${showCredentials.client_id}/credentials/resend`, {
        method: 'POST'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Error');
      
      if (data.credentials) {
        // Email failed, show credentials manually
        setNewCredentials({
          username: data.credentials.username,
          password: data.credentials.password,
          expires_at: data.credentials.expires_at
        });
        toast.success('Nuevas credenciales generadas - envía manualmente');
      } else {
        toast.success(data.message);
        setNewCredentials(null);
      }
      fetchClientCredentials(showCredentials.client_id);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const extendCredentials = async () => {
    try {
      const response = await adminFetch(`${API}/admin/clients/${showCredentials.client_id}/credentials/extend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days: extendDays })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Error');
      toast.success(`Vigencia extendida hasta: ${data.new_expires_at}`);
      fetchClientCredentials(showCredentials.client_id);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const regeneratePassword = async (credentialId) => {
    try {
      const response = await adminFetch(`${API}/admin/clients/${showCredentials.client_id}/credentials/${credentialId}/reset`, {
        method: 'PUT'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Error');
      
      setNewCredentials({
        username: clientCredentials[0]?.username,
        password: data.password
      });
      toast.success('Nueva contraseña generada');
      fetchClientCredentials(showCredentials.client_id);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const deleteCredentials = async (credentialId) => {
    if (!confirm('¿Eliminar estas credenciales? El cliente perderá el acceso.')) return;
    try {
      await adminFetch(`${API}/admin/clients/${showCredentials.client_id}/credentials/${credentialId}`, {
        method: 'DELETE'
      });
      toast.success('Credenciales eliminadas');
      setNewCredentials(null);
      fetchClientCredentials(showCredentials.client_id);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copiado al portapapeles');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  const filteredClients = statusFilter === 'all' 
    ? clients 
    : statusFilter === 'blocked' 
      ? clients.filter(c => blockedAccounts[c.client_id]) 
      : clients.filter(c => c.status === statusFilter);

  return (
    <div className="p-6 lg:p-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl mb-2">Clientes</h1>
            <p className="text-muted-foreground">Gestiona tu base de datos de clientes</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExport} className="gap-2" data-testid="export-clients-btn">
              <Download className="w-4 h-4" /> Exportar JSON
            </Button>
            <Button variant="outline" onClick={() => setShowMessages(true)} className="gap-2">
              <MessageSquare className="w-4 h-4" /> Mensajes ({messages.length})
            </Button>
            <Button onClick={() => openModal()} className="gap-2">
              <Plus className="w-4 h-4" /> Nuevo Cliente
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['all', 'potential', 'active', 'completed'].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
                statusFilter === status ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {status === 'all' ? 'Todos' : statusLabels[status]}
            </button>
          ))}
          <button
            onClick={() => setStatusFilter('blocked')}
            className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors flex items-center gap-1.5 ${
              statusFilter === 'blocked' ? 'bg-red-600 text-white' : 'text-red-500 hover:text-red-400'
            }`}
            data-testid="filter-blocked"
          >
            <ShieldAlert className="w-3.5 h-3.5" />
            Bloqueadas {Object.keys(blockedAccounts).length > 0 && `(${Object.keys(blockedAccounts).length})`}
          </button>
        </div>

        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-muted animate-pulse" />)}</div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-12 bg-card border border-border">
            <User className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No hay clientes en esta categoría</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredClients.map((client) => (
              <div key={client.client_id} className={`bg-card border p-4 flex items-center gap-4 group hover:border-foreground/20 transition-colors ${blockedAccounts[client.client_id] ? 'border-red-500/40' : 'border-border'}`}>
                <div className={`w-3 h-3 rounded-full ${statusColors[client.status]}`} />
                <div 
                  className="flex-1 min-w-0 cursor-pointer"
                  onClick={() => fetchClientDetail(client.client_id)}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{client.name}</span>
                    <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">{statusLabels[client.status]}</span>
                    {blockedAccounts[client.client_id] && (
                      <span className="text-xs px-2 py-0.5 bg-red-500/15 text-red-500 rounded flex items-center gap-1" data-testid={`blocked-badge-${client.client_id}`}>
                        <ShieldAlert className="w-3 h-3" />
                        Bloqueada ({blockedAccounts[client.client_id].attempts} intentos)
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {client.email}</span>
                    {client.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {client.phone}</span>}
                  </div>
                  {client.services_interested?.length > 0 && (
                    <div className="flex gap-1 mt-1">{client.services_interested.map(s => <span key={s} className="text-xs bg-muted px-2 py-0.5">{s}</span>)}</div>
                  )}
                </div>
                {blockedAccounts[client.client_id] && (
                  <button 
                    onClick={(e) => quickUnlock(client.client_id, e)}
                    disabled={unlocking === client.client_id}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-500/10 text-red-500 border border-red-500/30 rounded hover:bg-red-500/20 transition-colors disabled:opacity-50"
                    title="Desbloquear cuenta"
                    data-testid={`unlock-btn-${client.client_id}`}
                  >
                    <Unlock className="w-3.5 h-3.5" />
                    {unlocking === client.client_id ? 'Desbloqueando...' : 'Desbloquear'}
                  </button>
                )}
                <button 
                  onClick={() => fetchClientDetail(client.client_id)}
                  className="p-2 hover:bg-muted rounded hidden sm:flex items-center gap-1 text-sm text-muted-foreground"
                  title="Ver detalle"
                >
                  <Eye className="w-4 h-4" />
                  <span className="hidden lg:inline">Ver</span>
                </button>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openCredentialsModal(client)} className="p-2 hover:bg-muted rounded" title="Gestionar acceso">
                    <Key className="w-4 h-4 text-amber-500" />
                  </button>
                  <button onClick={() => openModal(client)} className="p-2 hover:bg-muted rounded"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(client.client_id)} className="p-2 hover:bg-muted rounded"><Trash2 className="w-4 h-4 text-destructive" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Client Modal */}
      {showModal && (
        <DraggableModal onClose={() => setShowModal(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-background border border-border w-[28rem] max-w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-medium">{editing ? 'Editar Cliente' : 'Nuevo Cliente'}</h2>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Nombre *</label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Email * (será el usuario de acceso)</label>
                <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Teléfono</label>
                <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Estado</label>
                <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full h-10 px-3 border border-input bg-background rounded-md">
                  {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Servicios de interés</label>
                <div className="flex flex-wrap gap-2">
                  {services.map(s => (
                    <button key={s} type="button" onClick={() => toggleService(s)}
                      className={`px-3 py-1 text-xs border ${form.services_interested.includes(s) ? 'bg-foreground text-background border-foreground' : 'border-border'}`}
                    >{s}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Notas</label>
                <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={3} />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button type="submit">Guardar</Button>
              </div>
            </form>
          </motion.div>
        </DraggableModal>
      )}

      {/* Credentials Modal */}
      {showCredentials && (
        <DraggableModal onClose={() => { setShowCredentials(null); setNewCredentials(null); }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-background border border-border w-[32rem] max-w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="font-medium">Acceso al Área Privada</h2>
                <p className="text-sm text-muted-foreground">{showCredentials.name} ({showCredentials.email})</p>
              </div>
              <button onClick={() => setShowCredentials(null)}><X className="w-5 h-5" /></button>
            </div>
            
            <div className="p-4 space-y-6">
              {/* Existing credentials */}
              {clientCredentials.length > 0 ? (
                <div>
                  <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Credenciales Actuales</h3>
                  {clientCredentials.map(cred => (
                    <div key={cred.credential_id} className="p-4 bg-muted/50 border border-border space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm"><strong>Usuario:</strong> {cred.username}</p>
                          {cred.password_plain ? (
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-sm">
                                <strong>Contraseña:</strong>{' '}
                                <span className="font-mono">{showPassword ? cred.password_plain : '••••••••••••'}</span>
                              </p>
                              <button onClick={() => setShowPassword(!showPassword)} className="p-1 hover:bg-muted rounded" data-testid="toggle-password">
                                {showPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                              </button>
                              <button onClick={() => copyToClipboard(cred.password_plain)} className="p-1 hover:bg-muted rounded" data-testid="copy-password">
                                {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                              </button>
                            </div>
                          ) : (
                            <p className="text-xs text-amber-500 mt-1">
                              Contraseña no disponible. Haz clic en "Reenviar" para generar una nueva.
                            </p>
                          )}
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          isExpired(cred.expires_at) 
                            ? 'bg-red-500/20 text-red-600' 
                            : cred.expires_at 
                              ? 'bg-green-500/20 text-green-600' 
                              : 'bg-amber-500/20 text-amber-600'
                        }`}>
                          {isExpired(cred.expires_at) ? 'Caducadas' : cred.expires_at ? 'Activas' : 'Pendiente envío'}
                        </span>
                      </div>
                      
                      {cred.expires_at && (
                        <p className="text-sm text-muted-foreground">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {isExpired(cred.expires_at) ? 'Caducaron' : 'Válidas hasta'}: {formatDate(cred.expires_at)}
                        </p>
                      )}
                      
                      <div className="flex gap-2 flex-wrap">
                        {!cred.expires_at && (
                          <Button size="sm" onClick={sendCredentialsEmail} disabled={sendingEmail} className="gap-2">
                            <Send className="w-3 h-3" />
                            {sendingEmail ? 'Enviando...' : 'Enviar por Email'}
                          </Button>
                        )}
                        {cred.expires_at && (
                          <>
                            <Button size="sm" onClick={resendCredentialsEmail} disabled={sendingEmail} className="gap-2">
                              <Send className="w-3 h-3" />
                              {sendingEmail ? 'Enviando...' : 'Reenviar credenciales'}
                            </Button>
                            <div className="flex items-center gap-2">
                              <Input 
                                type="number" 
                                value={extendDays} 
                                onChange={e => setExtendDays(parseInt(e.target.value) || 15)}
                                className="w-20 h-8"
                                min={1}
                              />
                              <Button size="sm" variant="outline" onClick={extendCredentials} className="gap-2">
                                <Clock className="w-3 h-3" />
                                Prorrogar
                              </Button>
                            </div>
                          </>
                        )}
                        <Button size="sm" variant="outline" onClick={() => regeneratePassword(cred.credential_id)} className="gap-2">
                          <RefreshCw className="w-3 h-3" />
                          Nueva contraseña
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => deleteCredentials(cred.credential_id)} className="text-destructive">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div>
                  <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Sin Credenciales</h3>
                  <p className="text-sm text-muted-foreground mb-4">Este cliente aún no tiene credenciales de acceso.</p>
                  <Button onClick={createCredentials} className="gap-2">
                    <Key className="w-4 h-4" />
                    Crear Credenciales
                  </Button>
                </div>
              )}

              {/* Show new credentials */}
              {newCredentials && (
                <div className="p-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Key className="w-4 h-4 text-amber-600" />
                    Credenciales Generadas
                  </h3>
                  <div className="space-y-2 font-mono text-sm">
                    <div className="flex items-center justify-between p-2 bg-background/50">
                      <span><strong>Usuario:</strong> {newCredentials.username}</span>
                      <button onClick={() => copyToClipboard(newCredentials.username)} className="p-1 hover:bg-background rounded">
                        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-background/50">
                      <span><strong>Contraseña:</strong> {showPassword ? newCredentials.password : '••••••••••••'}</span>
                      <div className="flex gap-1">
                        <button onClick={() => setShowPassword(!showPassword)} className="p-1 hover:bg-background rounded">
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button onClick={() => copyToClipboard(newCredentials.password)} className="p-1 hover:bg-background rounded">
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {newCredentials.expires_at && (
                      <div className="p-2 bg-background/50">
                        <strong>Válido hasta:</strong> {newCredentials.expires_at}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-3">
                    {clientCredentials[0]?.expires_at 
                      ? 'Recuerda enviar estas credenciales al cliente de forma segura.'
                      : 'Haz clic en "Enviar por Email" para activar las credenciales y enviarlas al cliente.'}
                  </p>
                </div>
              )}

              {/* Info box */}
              <div className="p-4 bg-muted/50 border border-border text-sm">
                <p className="font-medium mb-2">Información:</p>
                <ul className="text-muted-foreground space-y-1 text-xs">
                  <li>• El usuario de acceso es el email del cliente</li>
                  <li>• La contraseña se genera automáticamente (segura y única)</li>
                  <li>• Las credenciales tienen vigencia de 15 días desde el envío</li>
                  <li>• Puedes prorrogar el acceso en cualquier momento</li>
                </ul>
              </div>

              {/* Unlock Account */}
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-2 text-amber-500 border-amber-500/30 hover:bg-amber-500/10"
                onClick={async () => {
                  try {
                    const res = await adminFetch(`${API}/admin/clients/${showCredentials.client_id}/unlock`, {
                      method: 'POST'
                    });
                    if (res.ok) {
                      const data = await res.json();
                      toast.success(data.message);
                      setBlockedAccounts(prev => { const next = {...prev}; delete next[showCredentials.client_id]; return next; });
                    } else {
                      toast.error('Error al desbloquear');
                    }
                  } catch { toast.error('Error de conexión'); }
                }}
                data-testid="unlock-account"
              >
                <Unlock className="w-4 h-4" />
                Desbloquear cuenta (si bloqueada por intentos fallidos)
              </Button>
            </div>
          </motion.div>
        </DraggableModal>
      )}

      {/* Messages Modal */}
      {showMessages && (
        <DraggableModal onClose={() => setShowMessages(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-background border border-border w-[42rem] max-w-[calc(100vw-2rem)] max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-medium">Mensajes de Contacto</h2>
              <button onClick={() => setShowMessages(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-border">
              {messages.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">No hay mensajes</div>
              ) : messages.map((msg) => (
                <div key={msg.message_id} className="p-4">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div>
                      <span className="font-medium">{msg.name}</span>
                      <span className="text-sm text-muted-foreground ml-2">{msg.email}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{new Date(msg.created_at).toLocaleDateString('es-ES')}</span>
                  </div>
                  <p className="text-sm font-medium mb-1">{msg.subject}</p>
                  <p className="text-sm text-muted-foreground mb-3">{msg.message}</p>
                  <Button size="sm" variant="outline" onClick={() => convertToClient(msg.message_id)}>
                    <User className="w-3 h-3 mr-2" /> Convertir a cliente
                  </Button>
                </div>
              ))}
            </div>
          </motion.div>
        </DraggableModal>
      )}

      {/* Client Detail Modal */}
      {showClientDetail && clientDetail && (
        <DraggableModal onClose={() => { setShowClientDetail(null); setClientDetail(null); }}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="bg-background border border-border w-[42rem] max-w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 border-b border-border flex items-center justify-between sticky top-0 bg-background z-10">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${statusColors[clientDetail.client?.status]}`} />
                <div>
                  <h2 className="font-medium">{clientDetail.client?.name}</h2>
                  <p className="text-xs text-muted-foreground">{clientDetail.client?.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => { setShowClientDetail(null); openModal(clientDetail.client); }}>
                  <Pencil className="w-3 h-3 mr-2" /> Editar
                </Button>
                <button onClick={() => { setShowClientDetail(null); setClientDetail(null); }}><X className="w-5 h-5" /></button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground">Teléfono</label>
                  <p className="font-medium">{clientDetail.client?.phone || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Estado</label>
                  <p className="font-medium">{statusLabels[clientDetail.client?.status]}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-muted-foreground">Notas</label>
                  <p className="text-sm">{clientDetail.client?.notes || '-'}</p>
                </div>
              </div>

              {/* Events */}
              <div>
                <h3 className="font-medium flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4" /> Eventos ({clientDetail.events?.length || 0})
                </h3>
                {clientDetail.events?.length > 0 ? (
                  <div className="space-y-2">
                    {clientDetail.events.map(event => (
                      <div key={event.event_id} className="p-3 bg-muted/50 border border-border flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          event.service_type?.includes('Boda') ? 'bg-rose-500' :
                          event.service_type?.includes('Recién') || event.service_type?.includes('Bebé') ? 'bg-blue-500' :
                          event.service_type?.includes('Comunión') ? 'bg-purple-500' :
                          event.service_type?.includes('Embarazo') ? 'bg-pink-500' :
                          'bg-green-500'
                        }`} />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{event.title}</p>
                          <p className="text-xs text-muted-foreground">{event.service_type} • {event.location}</p>
                        </div>
                        <div className="text-right text-sm">
                          <p>{event.date}</p>
                          <p className="text-muted-foreground">{event.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No hay eventos</p>
                )}
              </div>

              {/* Session Data */}
              {clientDetail.session_data?.length > 0 && (
                <div>
                  <h3 className="font-medium flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4" /> Datos de Sesión ({clientDetail.session_data?.length || 0})
                  </h3>
                  <div className="space-y-2">
                    {clientDetail.session_data.map(sd => (
                      <div key={sd.session_data_id} className="p-3 bg-muted/50 border border-border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">{sessionTypeLabels[sd.session_type] || sd.session_type}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(sd.created_at).toLocaleDateString('es-ES')}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {Object.entries(sd.data || {}).slice(0, 6).map(([key, value]) => (
                            <div key={key}>
                              <span className="text-muted-foreground">{key.replace(/_/g, ' ')}:</span>{' '}
                              <span>{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Files */}
              {clientDetail.files?.length > 0 && (
                <div>
                  <h3 className="font-medium flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4" /> Archivos ({clientDetail.files?.length || 0})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {clientDetail.files.map(file => (
                      <span key={file.file_id} className="text-xs bg-muted px-2 py-1 rounded">
                        {file.title}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Testimonial */}
              {clientDetail.testimonial && (
                <div>
                  <h3 className="font-medium flex items-center gap-2 mb-3">
                    <Star className="w-4 h-4" /> Testimonio
                  </h3>
                  <div className="p-3 bg-muted/50 border border-border">
                    <div className="flex items-center gap-1 mb-2">
                      {[1, 2, 3, 4, 5].map(star => (
                        <Star 
                          key={star} 
                          className={`w-4 h-4 ${star <= clientDetail.testimonial.rating ? 'text-amber-500 fill-amber-500' : 'text-muted'}`} 
                        />
                      ))}
                    </div>
                    <p className="text-sm italic">"{clientDetail.testimonial.content}"</p>
                    <p className="text-xs text-muted-foreground mt-2">{clientDetail.testimonial.date}</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </DraggableModal>
      )}
    </div>
  );
};

export default AdminClients;
